import { SentryService } from '../../modules/infra/observability/sentry';
import Http from '../http';
import config from '../../config';
import { Logger } from '../../modules/infra/observability/logger';

class Terminal {
  constructor() {
    this.createTerminal();
    this.registerUnloadWatcher();
  }

  getName() {
    return 'Client-side';
  }

  createTerminal() {
    if (!window.StripeTerminal) {
      Logger.log('Stripe is down', {
        location: this.readerLocationId,
        simulated: this.terminalSimulatedMode,
        connectionStatus: null,
        paymentStatus: null,
        action: 'Stripe is down',
      });

      SentryService.captureException(new Error('Stripe module is not loaded. Most likely it is down'));

      return;
    }

    this.terminal = window.StripeTerminal.create({
      onFetchConnectionToken: () => Http.post('/pos/connection-token').then(res => res.secret),
      onUnexpectedReaderDisconnect: () => {
        if (this.terminal) {
          Logger.log('Unexpected reader disconnect', {
            location: this.readerLocationId,
            simulated: this.terminalSimulatedMode,
            connectionStatus: this.terminal.getConnectionStatus(),
            paymentStatus: this.terminal.getPaymentStatus(),
            action: 'Unexpected reader disconnect',
          });
        } else {
          Logger.log('Unexpected reader disconnect', {
            location: this.readerLocationId,
            simulated: this.terminalSimulatedMode,
            connectionStatus: null,
            paymentStatus: null,
            action: 'Unexpected reader disconnect',
          });
        }

        this.terminal = null;
      },
      onConnectionStatusChange: () => {
        if (this.terminal) {
          Logger.log('Connection status changed', {
            location: this.readerLocationId,
            simulated: this.terminalSimulatedMode,
            connectionStatus: this.terminal.getConnectionStatus(),
            paymentStatus: this.terminal.getPaymentStatus(),
            action: 'Connection status changed',
          });

          return;
        }

        Logger.log('Connection status changed', {
          location: this.readerLocationId,
          simulated: this.terminalSimulatedMode,
          connectionStatus: null,
          paymentStatus: null,
          action: 'Connection status changed',
        });
      },
      onPaymentStatusChange: () => {
        if (this.terminal) {
          Logger.log('Payment status changed', {
            location: this.readerLocationId,
            simulated: this.terminalSimulatedMode,
            connectionStatus: this.terminal.getConnectionStatus(),
            paymentStatus: this.terminal.getPaymentStatus(),
            action: 'Payment status changed',
          });

          return;
        }

        Logger.log('Payment status changed', {
          location: this.readerLocationId,
          simulated: this.terminalSimulatedMode,
          connectionStatus: null,
          paymentStatus: null,
          action: 'Payment status changed',
        });
      },
    });

    return this.terminal;
  }

  // force disposal when user refresh the page with a terminal waiting for card
  registerUnloadWatcher() {
    window.addEventListener('unload', () => this.dispose());
  }

  discoverReaders(readerLocationId) {
    if (!this.terminal) {
      this.createTerminal();
    }

    this.readerLocationId = readerLocationId;
    this.terminalSimulatedMode = config.terminalSimulatedMode;
    Logger.log('Discover readers', {
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      action: 'Discover readers',
    });

    return this.terminal.discoverReaders({
      location: readerLocationId,
      simulated: config.terminalSimulatedMode,
    });
  }

  connect(reader) {
    Logger.log('Connect to reader', {
      reader,
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      action: 'Connect to reader',
    });
    return this.terminal.connectReader(reader);
  }

  collectPayment = (paymentIntentSecret, externalConfigOverride = {}) => {
    Logger.log('Collect payment', {
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      action: 'Collect payment',
    });

    const configOverride = {};

    if (externalConfigOverride && externalConfigOverride.tipping) {
      configOverride.skip_tipping = externalConfigOverride.tipping.skipTipping;

      if (!externalConfigOverride.tipping.skipTipping) {
        configOverride.tipping = {
          eligible_amount: externalConfigOverride.tipping.amountEligible,
        };
      }
    }

    return this.terminal.collectPaymentMethod(paymentIntentSecret, {
      config_override: configOverride,
    });
  };

  collectRefundPayment = (chargeId, amount, currency) => {
    Logger.log('Collect refund payment', {
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      chargeId,
      amount,
      currency,
      action: 'Collect refund payment',
    });
    return this.terminal.collectRefundPaymentMethod(chargeId, amount, currency, {
      reverse_transfer: true,
    });
  };

  processPayment(paymentIntent) {
    Logger.log('Process payment', {
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      action: 'Process payment',
    });
    return this.terminal.processPayment(paymentIntent);
  }

  processRefund() {
    Logger.log('Process refund', {
      location: this.readerLocationId,
      simulated: this.terminalSimulatedMode,
      terminalType: this.getName(),
      connectionStatus: this.terminal.getConnectionStatus(),
      paymentStatus: this.terminal.getPaymentStatus(),
      action: 'Process refund',
    });
    return this.terminal.processRefund();
  }

  dispose(strategy) {
    if (this.terminal) {
      if (this.isWaitingForClient()) {
        switch (strategy) {
          case 'pay':
            this.terminal.cancelCollectPaymentMethod();
            break;
          case 'refund':
            this.terminal.cancelCollectRefundPaymentMethod();
            break;
          default:
        }
      }

      if (this.isConnected()) {
        this.terminal.disconnectReader();
      }
    }
  }

  isConnected = () => (
    !!this.terminal && this.terminal.getConnectionStatus() === window.StripeTerminal.ConnectionStatus.CONNECTED
  );

  isWaitingForClient = () => (
    !!this.terminal && this.terminal.getPaymentStatus() === window.StripeTerminal.PaymentStatus.WAITING_FOR_INPUT
  );
}

export default new Terminal();
